import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchControllers } from '@/features/controllers/api'
import { fetchAlarmTopics } from '@/features/alarmTopics/api'
import { fetchFleetOverview, fetchSiteSeverityRules } from '@/features/fleetAlarms/api'
import NotificationChannelsSection from '@/features/fleetAlarms/NotificationChannelsSection'
import SitePicker from '@/features/fleetAlarms/SitePicker'
import SubscribeBySeveritySection from '@/features/fleetAlarms/SubscribeBySeveritySection'
import BulkApplySection from '@/features/fleetAlarms/BulkApplySection'
import SeverityDefaultsSection from '@/features/fleetAlarms/SeverityDefaultsSection'
import { groupTopicsBySeverity, plural, siteListLabel } from '@/features/fleetAlarms/severityRules'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/** Ports includes/fleet_alarm_subscriptions_manage.html. Every section here
 * sets the signed-in user's own alarm notifications; none changes what they
 * can see or how alarm types are classified. Where settings overlap the most
 * specific wins: individual alarm > severity subscription for a site >
 * severity default (see UserAlarmSubscriptionServiceImplementation.resolve).
 * Severities and their alarm types come from the fleet overview and
 * GET /portal/settings/alarm-topics. */
export default function FleetAlarmSubscriptionsManagePage() {
  const overviewQuery = useQuery({ queryKey: ['fleet-alarm-overview'], queryFn: fetchFleetOverview })
  const controllersQuery = useQuery({ queryKey: ['controllers'], queryFn: fetchControllers })
  const topicsQuery = useQuery({ queryKey: ['alarm-topics'], queryFn: fetchAlarmTopics })
  const rulesQuery = useQuery({ queryKey: ['fleet-site-severity-rules'], queryFn: fetchSiteSeverityRules })

  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set())

  const severities = useMemo(() => overviewQuery.data?.severities ?? [], [overviewQuery.data])
  const topicGroups = useMemo(
    () => groupTopicsBySeverity(severities, topicsQuery.data?.alarm_topics ?? []),
    [severities, topicsQuery.data],
  )
  const controllers = useMemo(() => controllersQuery.data ?? [], [controllersQuery.data])
  const selectedIds = useMemo(
    () => controllers.map((c) => c.controller_id).filter((id) => selectedSites.has(id)),
    [controllers, selectedSites],
  )
  const siteNames = useMemo(() => {
    const byId = new Map(controllers.map((c) => [c.controller_id, c.controller_name || c.controller_id]))
    return selectedIds.map((id) => byId.get(id) ?? id)
  }, [controllers, selectedIds])

  const queries = [overviewQuery, controllersQuery, topicsQuery, rulesQuery]
  if (queries.some((q) => q.isLoading)) return <LoadingState label="Loading subscriptions…" />
  const failed = queries.find((q) => q.isError)
  if (failed) {
    return (
      <ErrorState
        message={failed.error instanceof Error ? failed.error.message : 'Failed to load.'}
        onRetry={() => queries.forEach((q) => q.isError && q.refetch())}
      />
    )
  }

  const rulesAvailable = Boolean(rulesQuery.data?.available && overviewQuery.data?.site_severity_rules_available)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Manage Fleet Subscriptions</h1>
          <p className="mt-1 max-w-3xl text-sm text-text-secondary">
            Choose which alarms <strong>you</strong> are notified about. These settings never change what you can see
            or how alarms are classified. Where they overlap, the most specific wins: an individual alarm setting, then
            a severity subscription for that site, then your severity default.
          </p>
        </div>
        <Link
          to="/fleet-alarm-subscriptions"
          className="rounded-full border border-nav-line px-4 py-2 text-sm font-semibold text-text-primary hover:bg-nav-hover"
        >
          View summary
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <aside className="lg:sticky lg:top-4 lg:self-start">
          <SitePicker controllers={controllers} selected={selectedSites} onChange={setSelectedSites} />
        </aside>

        <div className="min-w-0 space-y-6">
          <div
            className="sticky top-0 z-20 rounded-2xl border border-brand/40 bg-card px-4 py-2 text-sm text-text-primary shadow-sm"
            aria-live="polite"
            data-testid="selected-sites-bar"
          >
            {selectedIds.length === 0 ? (
              <span className="text-text-secondary">No sites selected. Choose sites to use sections 2 and 3.</span>
            ) : (
              <>
                <strong>{plural(selectedIds.length, 'site')} selected:</strong> {siteListLabel(siteNames, 4)}
              </>
            )}
          </div>

          <SubscribeBySeveritySection
            key={selectedIds.join('|')}
            severities={severities}
            topicGroups={topicGroups}
            selectedIds={selectedIds}
            siteNames={siteNames}
            rules={rulesQuery.data?.rules ?? []}
            available={rulesAvailable}
          />

          <BulkApplySection topicGroups={topicGroups} selectedIds={selectedIds} siteNames={siteNames} />
        </div>
      </div>

      <SeverityDefaultsSection severities={severities} />

      <NotificationChannelsSection />
    </div>
  )
}
